import { useState, useCallback, useEffect } from "react";
import { Validate } from "api/interfaces/commonInterface.interface";
import { type Option } from "utils/options";
import {
  checkWhiteSpaceRegEx,
  validateVimeoUrl,
  validateYoutubeUrl,
} from "utils/regEx";
import { ValidateMsg } from "api/interfaces/commonInterface.interface";
import {
  SelectGroup,
  VideoGuideSetting,
  VideoGuideVideoDto,
} from "api/interfaces/managerInterface.interface";
import useHelpGuideApi, { HelpGuideGroup } from "hook/useHelpGuideApi";
import { buildGuideItem, notifyByType } from "../../VideoManagerContainer";
import AddVideoPresenter from "./AddVideoPresenter";
import useApiError from "hook/useApiError";
import { checkEmpty } from "utils/functions";

interface Props {
  groups: HelpGuideGroup[];
  groupsIsFetching: boolean;
  isEdit?: boolean; // 편집 모드일 때
  selectedGroup: SelectGroup;
  onModal: (type?: string) => void;
  selectedVideo: VideoGuideSetting | undefined;
  onClickDeactive?: (userId: string) => void;
  maxPosition: number;
  getHelpGuides: () => void;
}

export enum UPDATE_TYPE {
  CATEGORY,
  GROUP,
  TITLE,
  VIDEO_URL,
  ORDER,
  DESC,
}

function AddVideoContainer(props: Props): JSX.Element {
  const [validate, setValidate] = useState<Validate>({
    title: false,
    videoUrl: false,
  });
  const { intlString, lang, notifyError } = useApiError();
  const [validateMsg, setValidateMsg] = useState<ValidateMsg>({
    title: "",
    videoUrl: "",
  });
  const [videoInfo, setVideoInfo] = useState<VideoGuideVideoDto>({
    groupId: props.selectedGroup.groupId,
    groupName: props.selectedGroup.groupName,
    title: "",
    videoUrl: "",
    thumbnailUrl: "",
    description: "",
    position: 1,
  });
  const { addHelpGuideItem, updateHelpGuideItem } = useHelpGuideApi();

  const [isInProcess, setIsInProcess] = useState<boolean>(false);

  useEffect(() => {
    if (!checkEmpty(props.selectedGroup.groupId)) {
      setVideoInfo((info) => {
        return { ...info, groupId: props.selectedGroup.groupId as string };
      });
    }
  }, [props.selectedGroup]);

  useEffect(() => {
    if (props.isEdit && props.selectedVideo !== undefined) {
      setVideoInfo({
        id: props.selectedVideo.id,
        groupId: props.selectedGroup.groupId,
        groupName: props.selectedGroup.groupName,
        title: props.selectedVideo.title,
        videoUrl: props.selectedVideo.videoUrl,
        thumbnailUrl: props.selectedVideo.thumbnailUrl,
        description: props.selectedVideo.description,
        position: props.selectedVideo.position,
      });
    }
  }, [props.isEdit, props.selectedGroup.groupId, props.selectedVideo]);

  useEffect(() => {
    if (props.isEdit && props.selectedVideo !== undefined) {
    }
  }, [props.isEdit, props.selectedVideo]);

  const onChangeUserInfo = useCallback(
    (type: UPDATE_TYPE, value: string | number | string[]) => {
      if (type === UPDATE_TYPE.TITLE) {
        setVideoInfo((info) => {
          return { ...info, title: value as string };
        });
      } else if (type === UPDATE_TYPE.VIDEO_URL) {
        // level 설정 client/server 상위 선택못하게 막아야 함
        setVideoInfo((info) => {
          return { ...info, videoUrl: value as string };
        });
      } else if (type === UPDATE_TYPE.ORDER) {
        setVideoInfo((info) => {
          return { ...info, position: value as number };
        });
      } else if (type === UPDATE_TYPE.DESC) {
        setVideoInfo((info) => {
          return { ...info, description: value as string };
        });
      } else if (type === UPDATE_TYPE.GROUP) {
        setVideoInfo((info) => {
          const groupFound = groupOptions.find(
            (group) => group.value === value
          );
          return {
            ...info,
            groupId: groupFound?.value as string,
            groupName: groupFound?.label as string,
          };
        });
      }
    },
    []
  );

  const onSaveBefore = useCallback(async () => {
    setValidate((info) => {
      return { ...info, title: false, videoUrl: false };
    });
    setValidateMsg((info) => {
      return { ...info, title: "", videoUrl: "" };
    });

    let checkedFlag = false;
    if (checkEmpty(videoInfo.title)) {
      checkedFlag = true;
      setValidate((info) => {
        return { ...info, title: true };
      });
      setValidateMsg((info) => {
        return {
          ...info,
          title: intlString(lang.label.subscription.sendEmail.title.empty),
        };
      });
    }

    if (
      checkWhiteSpaceRegEx(videoInfo.videoUrl) ||
      !(
        validateYoutubeUrl(videoInfo.videoUrl) ||
        validateVimeoUrl(videoInfo.videoUrl)
      )
    ) {
      checkedFlag = true;
      setValidate((info) => {
        return { ...info, videoUrl: true };
      });
      setValidateMsg((info) => {
        return {
          ...info,
          videoUrl: intlString(lang.validateMsg.manager.video.url),
        };
      });
    }
    if (!videoInfo.thumbnailUrl) {
      checkedFlag = true;
      setValidate((info) => {
        return { ...info, videoUrl: true };
      });
      setValidateMsg((info) => {
        return {
          ...info,
          videoUrl: intlString(lang.validateMsg.manager.video.thumbnail),
        };
      });
    }

    if (checkedFlag) {
      return;
    }

    setIsInProcess(true);
    const item = buildGuideItem(videoInfo, "youtube");
    const isUpdate = !!videoInfo.id;
    const action = isUpdate ? updateHelpGuideItem : addHelpGuideItem;
    return action(item)
      .then(() => {
        notifyByType("success", "youtube", isUpdate ? "update" : "create");
        props.getHelpGuides();
      })
      .catch(() =>
        notifyByType("error", "youtube", isUpdate ? "update" : "create")
      )
      .finally(() => {
        setIsInProcess(false);
        props.onModal();
      });
  }, [props, videoInfo]);

  const convertThumbnailUrl = useCallback(async () => {
    if (videoInfo.videoUrl !== "") {
      const thumb = await getThumb(videoInfo.videoUrl);
      if (thumb !== "") {
        setVideoInfo((info) => {
          return { ...info, thumbnailUrl: thumb as string };
        });
      } else {
        setVideoInfo((info) => {
          return { ...info, thumbnailUrl: "" };
        });
      }
    }
  }, [videoInfo.videoUrl]);

  const getThumb = async (url: string, size?: string) => {
    if (url === null) {
      return "";
    }
    const isYouTube = /youtu\.be|youtube\.com/.test(url);
    const isVimeo = /vimeo\.com/.test(url);

    if (isYouTube) {
      return getThumbnailYoutube(url, size);
    } else if (isVimeo) {
      const thumbnail = await getThumbnailVimeo(url);
      return thumbnail;
    } else {
      notifyError(lang.validateMsg.manager.video.url);
      return "";
    }
  };

  const getThumbnailYoutube = (url: string, size?: string) => {
    try {
      let video = "";
      size = size === null ? "big" : size;
      const results = url
        .replace(/(>|<)/gi, "")
        .split(/(vi\/|v=|\/v\/|youtu\.be\/|\/embed\/)/);
      if (results[2] !== undefined) {
        const videoUrlArray = results[2].split(/[^0-9a-z_-]/i);
        video = videoUrlArray[0];
      } else {
        video = results[0];
      }

      if (size === "small") {
        return "http://img.youtube.com/vi/" + video + "/2.jpg";
      }
      return "http://img.youtube.com/vi/" + video + "/0.jpg";
    } catch (error) {
      return "";
    }
  };

  const getThumbnailVimeo = async (url: string): Promise<string> => {
    try {
      // 여러가지 패턴 지원 (embed, player, normal, etc.)
      const patterns = [
        /vimeo\.com\/(?:.*\/)?([0-9]{6,})/, // vimeo.com/12345678 or vimeo.com/abc/12345678
        /player\.vimeo\.com\/video\/([0-9]{6,})/, // player.vimeo.com/video/12345678
      ];

      for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match && match[1]) {
          const apiUrl = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(
            url
          )}`;

          try {
            const response = await fetch(apiUrl);
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            return data.thumbnail_url;
          } catch (error) {
            console.error("Vimeo thumbnail fetch error:", error);
            return "";
          }
        }
      }

      return ""; // ID 못 찾았을 경우
    } catch (error) {
      console.error("Vimeo video ID extraction error:", error);
      return "";
    }
  };

  const onDropUrl = useCallback(
    async (event: React.DragEvent<HTMLInputElement>) => {
      event.preventDefault();
      const videoHtml = event.dataTransfer.getData("text/html");
      if (videoHtml) {
        let parser = new DOMParser();
        const doc = parser.parseFromString(videoHtml, "text/html");
        const link = doc.getElementsByClassName(
          "ytp-title-link yt-uix-sessionlink"
        );
        if (link !== undefined && link.length !== 0) {
          const title = link[0].innerHTML;
          const url = event.dataTransfer.getData("text/plain");

          if (title && videoInfo.title === "") {
            setVideoInfo((info) => {
              return { ...info, title: title as string };
            });
          }

          if (url) {
            const thumbnail = await getThumb(url);
            setVideoInfo((info) => {
              return {
                ...info,
                videoUrl: url as string,
                thumbnailUrl: thumbnail ?? "",
              };
            });
          }
        } else {
          const videoUrl = event.dataTransfer.getData("text/plain");
          const thumbnail = await getThumb(videoUrl);
          setVideoInfo((info) => {
            return {
              ...info,
              videoUrl: videoUrl as string,
              thumbnailUrl: thumbnail ?? "",
            };
          });
        }
      } else {
        const videoUrl = event.dataTransfer.getData("text/plain");
        const thumbnail = await getThumb(videoUrl);
        setVideoInfo((info) => {
          return {
            ...info,
            videoUrl: videoUrl as string,
            thumbnailUrl: thumbnail ?? "",
          };
        });
      }
    },
    [videoInfo.title]
  );

  const groupOptions = props.groups
    .filter((group: HelpGuideGroup) => group.layout === "slide")
    .map((group: HelpGuideGroup) => {
      return {
        value: group.groupId as string,
        label: group.groupName,
        order: group.sortOrder,
      };
    });

  return (
    <AddVideoPresenter
      isEdit={props.isEdit ?? false}
      onModal={props.onModal}
      groupOptions={groupOptions}
      groupOptionsIsFetching={props.groupsIsFetching}
      videoInfo={videoInfo}
      validate={validate}
      validateMsg={validateMsg}
      isInProcess={isInProcess}
      onChangeUserInfo={onChangeUserInfo}
      onDropUrl={onDropUrl}
      convertThumbnailUrl={convertThumbnailUrl}
      onSaveBefore={onSaveBefore}
      maxPosition={props.maxPosition}
    />
  );
}

export default AddVideoContainer;
